import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED, type RealtimeManager } from '@/shared/realtime';

import { bizprocKeys } from './api';

/** Realtime event names of the module (backend `BizprocEvents`, `TaskEvents`; docs/modules/bizproc.md §16). */
export const BIZPROC_EVENTS = {
  tasksChanged: 'bizproc.tasks-changed',
  instanceChanged: 'bizproc.instance-changed',
  definitionChanged: 'bizproc.definition-changed',
  automationChanged: 'bizproc.automation-changed',
} as const;

/** `bizproc.tasks-changed` to the assignees: ids only. */
export interface TasksChangedPayload {
  taskId: string;
  reason: string;
}

/** `bizproc.instance-changed` on `bizproc-instance:<id>` and `bizproc-doc:<type>:<id>`. */
export interface InstanceChangedPayload {
  instanceId: string;
  documentType: string | null;
  documentId: string | null;
  status: string;
  revision: number;
}

/** `bizproc.definition-changed` on `bizproc-definition:<id>`. */
export interface DefinitionChangedPayload {
  definitionId: string;
  reason: string;
}

/** `bizproc.automation-changed` on `bizproc-automation:<type>:<category>`: a board was saved. */
export interface AutomationChangedPayload {
  documentType: string;
  category: string;
  version: number;
}

/** Which cached queries an event makes stale (pure, unit tested). */
export function staleQueriesFor(
  type: string,
  payload: {
    instanceId?: string;
    definitionId?: string;
    documentType?: string | null;
    documentId?: string | null;
  } | null,
): (readonly unknown[])[] {
  switch (type) {
    case BIZPROC_EVENTS.tasksChanged:
      // A task made, answered, passed on or closed: the counter, the lists and the card; the instance's waits and log.
      return [bizprocKeys.tasks(), bizprocKeys.instances()];
    case BIZPROC_EVENTS.instanceChanged: {
      const keys: (readonly unknown[])[] = [['bizproc', 'instances', 'list'], bizprocKeys.tasks()];
      if (payload?.instanceId)
        keys.push(bizprocKeys.instance(payload.instanceId), bizprocKeys.history(payload.instanceId));
      // The «Robots» of the document's tab show the run of the stage's robots.
      if (payload?.documentType && payload.documentId)
        keys.push(bizprocKeys.documentRobots(payload.documentType, payload.documentId));
      return keys;
    }
    case BIZPROC_EVENTS.definitionChanged: {
      // `reason: schedule`: a schedule of the process was changed or ran — its list and its history.
      const keys: (readonly unknown[])[] = [bizprocKeys.definitions(), bizprocKeys.schedules()];
      if (payload?.definitionId) keys.push(bizprocKeys.definition(payload.definitionId));
      return keys;
    }
    case BIZPROC_EVENTS.automationChanged:
      // A board saved by somebody: the board, how many wait for its robots and the robots of the documents' tabs.
      return [bizprocKeys.automation()];
    default:
      return [];
  }
}

/**
 * Keeps the module's caches live: `bizproc.*` events (to the assignees, and on the topics of an open instance, document
 * or definition) invalidate what they affect; a revoked topic drops what the user may no longer see; a reconnect
 * refetches everything.
 */
export function registerBizprocRealtime(realtime: RealtimeManager, queryClient: QueryClient): () => void {
  const offEvents = realtime.on<{
    instanceId?: string;
    definitionId?: string;
    documentType?: string | null;
    documentId?: string | null;
  } | null>('bizproc.*', (payload, envelope) => {
    for (const queryKey of staleQueriesFor(envelope.type, payload)) {
      void queryClient.invalidateQueries({ queryKey });
    }
  });
  const offRevoked = realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) => {
    const topic = payload?.topic;
    if (!topic?.startsWith('bizproc-')) return;
    void queryClient.resetQueries({ queryKey: bizprocKeys.instances() });
    void queryClient.invalidateQueries({ queryKey: bizprocKeys.tasks() });
    void queryClient.invalidateQueries({ queryKey: bizprocKeys.definitions() });
  });
  const offReconnect = realtime.on(REALTIME_RECONNECTED, () => {
    void queryClient.invalidateQueries({ queryKey: bizprocKeys.all });
  });
  return () => {
    offEvents();
    offRevoked();
    offReconnect();
  };
}
